import { useEffect } from 'react';

import Logger from '@eneco/common/log';
import { getCustomerProfile } from '@eneco/dc/services';

import { GTM_PLATFORM_VERSION } from '../constants';
import { useGTM } from './useGTM';
type GetCustomerProfileParams = Parameters<typeof getCustomerProfile>[0];

export const useEnrichedCustomerData = ({
  customerId,
  accountId,
  businessUnit,
  label,
  encodedCustomerId,
  isInitialized,
}: {
  customerId?: string;
  accountId?: number | null;
  businessUnit?: string;
  label?: string;
  encodedCustomerId?: string;
  isInitialized: boolean;
}) => {
  const { pushToDataLayer } = useGTM();

  useEffect(() => {
    const hasRequiredData = isInitialized && customerId != null && accountId != null && !!businessUnit && !!label;
    if (!hasRequiredData) return;

    const enrichData = async () => {
      const payload = {
        businessUnit: businessUnit as GetCustomerProfileParams['businessUnit'],
        label: label as GetCustomerProfileParams['label'],
        customerId: Number.parseInt(customerId),
      };
      let response: Awaited<ReturnType<typeof getCustomerProfile>>;
      try {
        response = await getCustomerProfile(payload);
      } catch (error) {
        Logger.warn('AW6ljz', 'Failed to fetch customer profile for GTM enrichment', error as Error);
        return;
      }
      // eslint-disable-next-line
      const account = response?.data?.accounts?.find(acc => acc.accountId === accountId);

      pushToDataLayer({
        clientId: customerId ? `${customerId}` : undefined,
        clientIdEncoded: encodedCustomerId,
        customerType: response?.data?.customerType,
        platformVersion: GTM_PLATFORM_VERSION,
        currentAccount: {
          accountId: account?.accountId,
          customerProfileType: account?.customerProfileType,
          productTypes: account?.productTypes,
          hasRedelivery: account?.hasRedelivery,
        },
      });
    };

    void enrichData();
  }, [encodedCustomerId, customerId, accountId, businessUnit, label, isInitialized, pushToDataLayer]);
};
